/**
 * @fileoverview Form state and handlers for the registration CorporateIdentity section.
 * @module components/AppointmentRegistrationSections/CorporateIdentity/useCorporateIdentityForm
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { logger, sanitizeError } from "../../../utils/logger.js";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import {
  buildEmptyDescriptions,
  normalizeCompanyDescriptions,
} from "../../../utils/corporateIdentityUtils.js";
import useCorporateIdentityReferenceData from "./useCorporateIdentityReferenceData.js";
import {
  getOperationEnvironmentOptions,
  getCompanySizeOptions,
  getCompanyGeneralActivityOptions,
} from "../../../data/selectOptions";
import { getRegistrationViewport } from "./useRegistrationViewport.js";
import { useCorporateIdentityLogo } from "./useCorporateIdentityLogo.js";
import { useCorporateIdentityChangeHandlers } from "./useCorporateIdentityChangeHandlers.js";

const initialDescriptions = (company) => {
  try {
    return normalizeCompanyDescriptions(company?.descripcionDeLaEmpresa);
  } catch (parseError) {
    logger.error("Error parsing descriptions:", {
      err: sanitizeError(parseError),
    });
    return buildEmptyDescriptions();
  }
};

/**
 * @param {Object} options
 * @param {Object} options.company
 * @param {Function} options.onFieldChange
 * @param {Function} options.onValidationChange
 */
export const useCorporateIdentityForm = ({
  company,
  onFieldChange,
  onValidationChange,
}) => {
  const { t, i18n } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const { isMobile } = getRegistrationViewport();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [razonSocial, setRazonSocial] = useState(company?.razonSocial || "");
  const [ambitoOperaciones, setAmbitoOperaciones] = useState(
    company?.ambitoOperaciones || null,
  );
  const [tamanoDeLaEmpresa, setTamanoDeLaEmpresa] = useState(
    company?.tamanoDeLaEmpresa || null,
  );
  const [ramaSCIAN, setRamaSCIAN] = useState(company?.ramaSCIAN ?? null);
  const [sectorEconomico, setSectorEconomico] = useState(
    company?.sectorEconomico ?? null,
  );
  const [subSectorSCIAN, setSubSectorSCIAN] = useState(
    company?.subSectorSCIAN ?? null,
  );
  const [grupoEmpresarial, setGrupoEmpresarial] = useState(
    company?.grupoEmpresarial || null,
  );
  const [actividadGeneralDeLaEmpresa, setActividadGeneralDeLaEmpresa] =
    useState(company?.actividadGeneralDeLaEmpresa || null);
  const [descriptions, setDescriptions] = useState(() =>
    initialDescriptions(company),
  );

  const {
    fieldValues,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    description: "",
    nombreComercial: company?.nombreComercial || "",
  });

  const {
    businessGroups,
    sectors,
    subSectors,
    branches,
    setSubsectors,
    setBranches,
  } = useCorporateIdentityReferenceData({
    sectorEconomico,
    subSectorSCIAN,
    ramaSCIAN,
  });

  const companyDescriptions = useMemo(() => {
    if (company?.descripcionDeLaEmpresa) {
      return company.descripcionDeLaEmpresa;
    }
    return buildEmptyDescriptions();
  }, [company?.descripcionDeLaEmpresa]);

  useEffect(() => {
    setDescriptions(companyDescriptions);
  }, [companyDescriptions]);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (company?.nombreComercial !== undefined) {
      handleFieldChange("nombreComercial", company.nombreComercial);
    }
    if (company?.razonSocial !== undefined) {
      setRazonSocial(company.razonSocial);
    }
    if (company?.ambitoOperaciones !== undefined) {
      setAmbitoOperaciones(company.ambitoOperaciones);
    }
    if (company?.tamanoDeLaEmpresa !== undefined) {
      setTamanoDeLaEmpresa(company.tamanoDeLaEmpresa);
    }
    if (company?.sectorEconomico !== undefined) {
      setSectorEconomico(company.sectorEconomico);
    }
    if (company?.subSectorSCIAN !== undefined) {
      setSubSectorSCIAN(company.subSectorSCIAN);
    }
    if (company?.ramaSCIAN !== undefined) {
      setRamaSCIAN(company.ramaSCIAN);
    }
  }, [
    company?.nombreComercial,
    company?.razonSocial,
    company?.ambitoOperaciones,
    company?.tamanoDeLaEmpresa,
    company?.sectorEconomico,
    company?.subSectorSCIAN,
    company?.ramaSCIAN,
    handleFieldChange,
  ]);

  const handleInputChange = useCallback(
    (field, value) => {
      if (onFieldChange) {
        onFieldChange(field, value);
      }
    },
    [onFieldChange],
  );

  const logo = useCorporateIdentityLogo({ company, t, handleInputChange });
  const handlers = useCorporateIdentityChangeHandlers({
    handleInputChange,
    handleFieldChange,
    descriptions,
    currentLanguage,
    setDescriptions,
    setSectorEconomico,
    setSubsectors,
    setSubSectorSCIAN,
    setBranches,
    setRamaSCIAN,
    setGrupoEmpresarial,
    setRazonSocial,
    setActividadGeneralDeLaEmpresa,
    setAmbitoOperaciones,
    setTamanoDeLaEmpresa,
  });

  useEffect(() => {
    if (!onValidationChange) {
      return;
    }
    const hasAnyErrors = hasErrors || Boolean(logo.error);
    if (prevHasErrorsRef.current !== hasAnyErrors) {
      prevHasErrorsRef.current = hasAnyErrors;
      onValidationChange(hasAnyErrors);
    }
  }, [hasErrors, logo.error, onValidationChange]);

  return {
    t,
    isMobile,
    fieldValues,
    razonSocial,
    descriptions,
    currentLanguage,
    ambitoOperaciones,
    tamanoDeLaEmpresa,
    actividadGeneralDeLaEmpresa,
    sectorEconomico,
    subSectorSCIAN,
    ramaSCIAN,
    grupoEmpresarial,
    businessGroups,
    sectors,
    subSectors,
    branches,
    operationEnvironmentOptions: getOperationEnvironmentOptions(t),
    companySizeOptions: getCompanySizeOptions(t),
    companyGeneralActivityOptions: getCompanyGeneralActivityOptions(t),
    getErrorMessage,
    getFieldValidationState,
    ...logo,
    ...handlers,
  };
};
